import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useTableFilters, ColumnFilters } from './useTableFilters';
import { useUniqueValues } from './useUniqueValues';
import { useSpreadsheetData } from './useSpreadsheetData';

export interface UseSpreadsheetConfigOptions<T extends { _id: string }> {
  data: T[];
  columns: ColumnDef<T & { id: string }>[];
  filterableColumns?: string[];
  getUniqueValue?: (item: T, column: string) => string;
  filterFn?: (item: T, filters: ColumnFilters) => boolean;
  enableFiltering?: boolean;
}

export interface UseSpreadsheetConfigReturn<T extends { _id: string }> {
  // Data
  transformedData: Array<T & { id: string }>;
  filteredData: Array<T & { id: string }>;
  
  // Filters
  columnFilters: ColumnFilters;
  setColumnFilters: (filters: ColumnFilters | ((prev: ColumnFilters) => ColumnFilters)) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  uniqueValues: Record<string, string[]>;
  
  // Spreadsheet props
  spreadsheetProps: {
    data: Array<T & { id: string }>;
    columns: ColumnDef<T & { id: string }>[];
    enableFiltering?: boolean;
    filterableColumns?: string[];
    uniqueValues: Record<string, string[]>;
    columnFilters: ColumnFilters;
    onColumnFiltersChange: (filters: ColumnFilters) => void;
  };
}

/**
 * Comprehensive hook for setting up Spreadsheet components
 * Combines data transformation, filtering, and unique values calculation
 * 
 * @param options - Configuration for Spreadsheet setup
 * @returns All data, filters, and props needed for Spreadsheet
 * 
 * @example
 * const spreadsheet = useSpreadsheetConfig({
 *   data: users,
 *   columns: userColumns,
 *   filterableColumns: ['role', 'isAdmin', 'isMockUser'],
 *   enableFiltering: true
 * });
 * 
 * <Spreadsheet {...spreadsheet.spreadsheetProps} />
 */
export const useSpreadsheetConfig = <T extends { _id: string }>({
  data,
  columns,
  filterableColumns = [],
  getUniqueValue,
  filterFn,
  enableFiltering = false,
}: UseSpreadsheetConfigOptions<T>): UseSpreadsheetConfigReturn<T> => {
  // Table filters
  const { columnFilters, setColumnFilters, clearFilters, hasActiveFilters } = useTableFilters();

  // Unique values for filters
  const { uniqueValues } = useUniqueValues<T>({
    data,
    columns: filterableColumns,
    getValue: getUniqueValue,
  });

  // Transform and filter data
  const { transformedData, filteredData } = useSpreadsheetData<T>({
    data,
    columnFilters,
    filterFn,
  });

  // Spreadsheet props
  const spreadsheetProps = useMemo(() => ({
    data: filteredData,
    columns,
    enableFiltering: enableFiltering && filterableColumns.length > 0,
    filterableColumns: enableFiltering ? filterableColumns : undefined,
    uniqueValues,
    columnFilters,
    onColumnFiltersChange: setColumnFilters,
  }), [filteredData, columns, enableFiltering, filterableColumns, uniqueValues, columnFilters, setColumnFilters]);

  return {
    transformedData,
    filteredData,
    columnFilters,
    setColumnFilters,
    clearFilters,
    hasActiveFilters,
    uniqueValues,
    spreadsheetProps,
  };
};
